import { useEffect, useState, useRef } from 'react';
import { toast } from 'sonner';
import { apiFetch } from '../lib/apiClient';

interface BreachResponse {
  breached: boolean;
  ttaBreached?: boolean;
  ttrBreached?: boolean;
  ttaPolicy?: number;
  ttrPolicy?: number;
  elapsed?: number;
}

interface CachedBreach { at: number; data: BreachResponse }
const cache = new Map<string, CachedBreach>();
const CACHE_TTL_MS = 60_000;
const REFRESH_INTERVAL_MS = 60_000;

const WARN_RATIO = 0.8;
const ALERT_MINS = 15;

function getElapsedMins(createdAt: string): number {
  return (Date.now() - new Date(createdAt).getTime()) / 60_000;
}

function formatRemaining(mins: number): string {
  if (mins <= 0) return 'now';
  if (mins < 60) return `${Math.round(mins)}m`;
  if (mins < 24 * 60) return `${Math.round(mins / 60)}h`;
  return `${(mins / 1440).toFixed(1)}d`;
}

export function SlaBreachBadge({
  customerId,
  severity,
  status,
  createdAt,
  incidentNumber,
  incidentTitle,
}: {
  customerId?: string;
  severity: string;
  status: string;
  createdAt: string;
  incidentNumber?: string;
  incidentTitle?: string;
}) {
  const [policy, setPolicy] = useState<BreachResponse | null>(null);
  const [, setTick] = useState(0);
  const alertedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 60_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!customerId || !createdAt || status === 'Closed') { setPolicy(null); return; }
    const cid = customerId;
    const created = createdAt;
    const key = `${cid}|${severity}|${status}|${created.slice(0, 16)}`;
    let cancelled = false;
    let lastIssuedAt = 0;

    function fetchOnce(): void {
      const cached = cache.get(key);
      if (cached && Date.now() - cached.at < CACHE_TTL_MS) {
        if (!cancelled) setPolicy(cached.data);
        return;
      }
      const issuedAt = Date.now();
      lastIssuedAt = issuedAt;
      apiFetch<BreachResponse>(
        `/sla/breach-check?customerId=${encodeURIComponent(cid)}&severity=${encodeURIComponent(severity)}&status=${encodeURIComponent(status)}&createdAt=${encodeURIComponent(created)}`,
      )
        .then((d) => {
          if (cancelled || issuedAt !== lastIssuedAt) return;
          cache.set(key, { at: Date.now(), data: d });
          setPolicy(d);
        })
        .catch(() => {});
    }

    fetchOnce();
    const timer = setInterval(fetchOnce, REFRESH_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [customerId, severity, status, createdAt]);

  useEffect(() => {
    if (!policy || policy.breached || status === 'Closed' || status === 'Redirected') return;

    const elapsed = getElapsedMins(createdAt);
    const ttaPolicy = policy.ttaPolicy ?? 0;
    const ttrPolicy = policy.ttrPolicy ?? 0;
    const ttaInPlay = status === 'New' && ttaPolicy > 0;
    const ttaRemaining = ttaInPlay ? Math.max(0, ttaPolicy - elapsed) : Infinity;
    const ttrRemaining = ttrPolicy > 0 ? Math.max(0, ttrPolicy - elapsed) : Infinity;
    const minRemaining = Math.min(ttaRemaining, ttrRemaining);
    const which = ttaRemaining <= ttrRemaining ? 'TTA' : 'TTR';

    if (minRemaining > 0 && minRemaining <= ALERT_MINS) {
      const alertKey = `${customerId}|${incidentNumber ?? createdAt}|${which}`;
      if (!alertedRef.current.has(alertKey)) {
        alertedRef.current.add(alertKey);
        toast.warning(
          `SLA breach in ${Math.round(minRemaining)} minute${Math.round(minRemaining) === 1 ? '' : 's'}`,
          {
            description: incidentNumber
              ? `#${incidentNumber}${incidentTitle ? ` · ${incidentTitle.slice(0, 70)}` : ''} · ${which} deadline`
              : `${severity} incident · ${which} deadline approaching`,
            duration: 15_000,
          },
        );
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policy, createdAt, status, customerId, incidentNumber, incidentTitle]);

  if (!policy) return null;

  const elapsed = getElapsedMins(createdAt);

  if (policy.breached) {
    const which = policy.ttrBreached ? 'TTR' : 'TTA';
    // Duration past the SLA deadline, not total incident age: elapsed minus
    // whichever target (TTA or TTR) was actually breached.
    const overdue = Math.max(0, elapsed - ((policy.ttrBreached ? policy.ttrPolicy : policy.ttaPolicy) ?? 0));
    return (
      <span
        className="inline-block px-1.5 py-0.5 text-[9px] font-bold rounded bg-red-500/30 text-red-700 dark:text-red-300 border border-red-500/50 tabular-nums"
        title={`Elapsed ${Math.round(elapsed)}m · TTA target ${policy.ttaPolicy}m · TTR target ${policy.ttrPolicy}m · SLA exceeded by ${formatRemaining(overdue)}`}
      >
        {which} breached {formatRemaining(overdue)} ago
      </span>
    );
  }

  const ttaPolicy = policy.ttaPolicy ?? 0;
  const ttrPolicy = policy.ttrPolicy ?? 0;
  const ttaInPlay = status === 'New' && ttaPolicy > 0;
  const ttaWarn = ttaInPlay && elapsed >= ttaPolicy * WARN_RATIO;
  const ttrWarn = ttrPolicy > 0 && elapsed >= ttrPolicy * WARN_RATIO;

  if (ttaWarn || ttrWarn) {
    const which = ttaWarn ? 'TTA' : 'TTR';
    const remaining = ttaWarn
      ? Math.max(0, ttaPolicy - elapsed)
      : Math.max(0, ttrPolicy - elapsed);
    return (
      <span
        className="inline-block px-1.5 py-0.5 text-[9px] font-bold rounded bg-amber-500/25 text-amber-700 dark:text-amber-300 border border-amber-500/40 tabular-nums"
        title={`SLA budget almost gone. ${which} target ${ttaWarn ? ttaPolicy : ttrPolicy}m, ${Math.round(elapsed)}m elapsed, ~${formatRemaining(remaining)} left`}
      >
        Due in {formatRemaining(remaining)}
      </span>
    );
  }

  return null;
}
